import React, { useCallback, useEffect, useState } from "react";
import {
  MdAdd,
  MdEdit,
  MdDelete,
  MdSave,
  MdClose,
  MdRestartAlt,
  MdQuestionAnswer,
} from "react-icons/md";
import Card, { CardHeader } from "components/card";
import Button, { IconButton } from "components/button";
import Badge from "components/badge";
import Dialog from "components/dialog";
import { useConfirm } from "components/dialog/ConfirmProvider";
import { InputField, Select, Textarea } from "components/fields";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState, ErrorState } from "components/states";
import notify from "lib/notify";
import { statusTone } from "lib/statusTone";
import { getApiErrorMessage } from "api";

const QUESTION_TYPES = [
  { value: "text", label: "Text Input" },
  { value: "rating", label: "Rating Scale (1-5)" },
  { value: "multiple_choice", label: "Multiple Choice" },
];

const EMPTY_FORM = {
  questionText: "",
  questionType: "text",
  placeholder: "",
  maxLength: 500,
  lowLabel: "",
  highLabel: "",
  options: [],
};

const QUESTION_FORM_ID = "pulse-question-form";

/**
 * Check-in (pulse) question list and editor for one assessment audience.
 *
 * @param {object} props
 * @param {object} props.config Audience config from `components/assessment/audiences`.
 */
export default function PulseQuestionsSettings({ config }) {
  const { service } = config;
  const confirm = useConfirm();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [isNewQuestion, setIsNewQuestion] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [textError, setTextError] = useState("");
  const [newOption, setNewOption] = useState("");

  // The first load drives the skeleton/error state; refreshes after a
  // mutation keep the list in place and report failures as a toast.
  const fetchQuestions = useCallback(
    async ({ initial = false } = {}) => {
      if (initial) {
        setLoading(true);
        setLoadError(null);
      }
      try {
        const response = await service.getPulseQuestions({ includeInactive: "true" });
        if (response.success) {
          setQuestions(response.data || []);
        }
      } catch (err) {
        if (initial) {
          setLoadError(err);
        } else {
          notify.error(getApiErrorMessage(err, "Could not refresh questions"));
        }
      } finally {
        if (initial) setLoading(false);
      }
    },
    [service]
  );

  useEffect(() => {
    fetchQuestions({ initial: true });
  }, [fetchQuestions]);

  const isEditorOpen = Boolean(editingQuestion) || isNewQuestion;

  const handleEdit = (question) => {
    setEditingQuestion(question);
    setIsNewQuestion(false);
    setTextError("");
    setNewOption("");
    setFormData({
      questionText: question.question || "",
      questionType: question.type || "text",
      placeholder: question.placeholder || "",
      maxLength: question.maxLength || 500,
      lowLabel: question.lowLabel || "",
      highLabel: question.highLabel || "",
      options: question.options || [],
    });
  };

  const handleAddNew = () => {
    setEditingQuestion(null);
    setIsNewQuestion(true);
    setTextError("");
    setNewOption("");
    setFormData({
      ...EMPTY_FORM,
      lowLabel: "Strongly Disagree",
      highLabel: "Strongly Agree",
    });
  };

  const handleCancel = () => {
    setEditingQuestion(null);
    setIsNewQuestion(false);
    setTextError("");
    setNewOption("");
    setFormData(EMPTY_FORM);
  };

  const closeEditor = () => {
    if (saving) return;
    handleCancel();
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!formData.questionText.trim()) {
      setTextError("Question text is required");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        questionText: formData.questionText,
        questionType: formData.questionType,
        placeholder: formData.questionType === "text" ? formData.placeholder : null,
        maxLength: formData.questionType === "text" ? formData.maxLength : null,
        lowLabel: formData.questionType === "rating" ? formData.lowLabel : null,
        highLabel: formData.questionType === "rating" ? formData.highLabel : null,
        options: formData.questionType === "multiple_choice" ? formData.options : null,
      };

      if (isNewQuestion) {
        await service.createPulseQuestion(payload);
        notify.success("Question created");
      } else {
        await service.updatePulseQuestion(editingQuestion.id, payload);
        notify.success("Question saved");
      }

      handleCancel();
      fetchQuestions();
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not save question"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (question) => {
    if (question.isSystem) {
      notify.error("System questions cannot be deleted");
      return;
    }

    const confirmed = await confirm({
      title: `Delete "${question.question}"?`,
      message: "This question will no longer appear in check-ins. This cannot be undone.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      await service.deletePulseQuestion(question.id);
      notify.success("Question deleted");
      fetchQuestions();
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not delete question"));
    }
  };

  const handleCopyDefaults = async () => {
    const confirmed = await confirm({
      title: "Copy Default Questions?",
      message: "This copies the default questions to your organization so you can edit them.",
      confirmLabel: "Copy Defaults",
    });
    if (!confirmed) return;

    try {
      await service.copyDefaultPulseQuestions();
      notify.success("Default questions copied");
      fetchQuestions();
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not copy default questions"));
    }
  };

  const addOption = () => {
    if (newOption.trim()) {
      setFormData({
        ...formData,
        options: [...formData.options, newOption.trim()],
      });
      setNewOption("");
    }
  };

  const removeOption = (index) => {
    setFormData({
      ...formData,
      options: formData.options.filter((_, i) => i !== index),
    });
  };

  if (loading) {
    return (
      <Card padding="md" aria-busy="true">
        <Skeleton className="mb-6 h-6 w-48" />
        <div className="flex flex-col gap-3">
          <Skeleton className="h-20 w-full rounded-control" />
          <Skeleton className="h-20 w-full rounded-control" />
          <Skeleton className="h-20 w-full rounded-control" />
        </div>
      </Card>
    );
  }

  if (loadError) {
    return (
      <Card padding="md">
        <ErrorState
          message={getApiErrorMessage(loadError, "Could not load questions")}
          onRetry={() => fetchQuestions({ initial: true })}
        />
      </Card>
    );
  }

  const isUsingDefaults = !questions.some((q) => !q.isSystem);

  let headerActions = null;
  if (questions.length > 0) {
    headerActions = isUsingDefaults ? (
      <Button variant="secondary" leftIcon={<MdRestartAlt />} onClick={handleCopyDefaults}>
        Customize Questions
      </Button>
    ) : (
      <Button leftIcon={<MdAdd />} onClick={handleAddNew}>
        Add Question
      </Button>
    );
  }

  return (
    <>
      <Card padding="md">
        <CardHeader
          title="Check-In Questions"
          description={
            isUsingDefaults
              ? "Using the system default questions. Choose Customize Questions to create your own."
              : "Custom questions for your organization."
          }
          actions={headerActions}
          className="mb-6"
        />

        {questions.length === 0 ? (
          <EmptyState
            icon={MdQuestionAnswer}
            title="No Questions Yet"
            description="Copy the default check-in questions to your organization, then edit them to fit your team."
            action={
              <Button leftIcon={<MdRestartAlt />} onClick={handleCopyDefaults}>
                Copy Defaults
              </Button>
            }
          />
        ) : (
          <ol className="flex flex-col gap-3">
            {questions.map((question, index) => {
              const status = question.isActive ? "Active" : "Inactive";
              return (
                <li
                  key={question.id}
                  className={`rounded-control border border-line p-4 ${
                    question.isActive ? "" : "bg-surface-subtle"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      aria-hidden="true"
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-control bg-surface-subtle text-sm font-bold text-fg-muted"
                    >
                      {index + 1}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p
                        className={`font-medium ${
                          question.isActive ? "text-fg" : "text-fg-muted"
                        }`}
                      >
                        {question.question}
                      </p>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <Badge tone="brand">
                          {QUESTION_TYPES.find((t) => t.value === question.type)?.label ||
                            question.type}
                        </Badge>
                        <Badge tone={statusTone(status)}>{status}</Badge>
                        {question.isSystem && <Badge tone="neutral">System Default</Badge>}
                        {question.type === "rating" && (
                          <span className="text-xs text-fg-muted">
                            {question.lowLabel} &rarr; {question.highLabel}
                          </span>
                        )}
                      </div>
                    </div>
                    {!question.isSystem && (
                      <div className="flex gap-1">
                        <IconButton
                          icon={<MdEdit />}
                          label="Edit Question"
                          onClick={() => handleEdit(question)}
                        />
                        <IconButton
                          icon={<MdDelete />}
                          label="Delete Question"
                          className="text-danger-fg"
                          onClick={() => handleDelete(question)}
                        />
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </Card>

      <Dialog
        isOpen={isEditorOpen}
        onClose={closeEditor}
        title={isNewQuestion ? "Add Question" : "Edit Question"}
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={closeEditor} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" form={QUESTION_FORM_ID} leftIcon={<MdSave />} loading={saving}>
              Save Question
            </Button>
          </>
        }
      >
        <form id={QUESTION_FORM_ID} onSubmit={handleSave} className="flex flex-col gap-4">
          <Select
            label="Question Type"
            value={formData.questionType}
            onChange={(e) => setFormData({ ...formData, questionType: e.target.value })}
            options={QUESTION_TYPES}
          />

          <Textarea
            label="Question Text"
            required
            rows={3}
            value={formData.questionText}
            onChange={(e) => {
              setFormData({ ...formData, questionText: e.target.value });
              if (textError) setTextError("");
            }}
            error={textError}
            placeholder="Enter your question"
          />

          {formData.questionType === "text" && (
            <>
              <InputField
                label="Placeholder Text"
                value={formData.placeholder}
                onChange={(e) => setFormData({ ...formData, placeholder: e.target.value })}
                placeholder="Enter placeholder text"
              />
              <InputField
                label="Max Character Length"
                type="number"
                min={50}
                max={2000}
                value={formData.maxLength}
                onChange={(e) =>
                  setFormData({ ...formData, maxLength: parseInt(e.target.value, 10) || 500 })
                }
              />
            </>
          )}

          {formData.questionType === "rating" && (
            <div className="grid grid-cols-2 gap-4">
              <InputField
                label="Low End Label (1)"
                value={formData.lowLabel}
                onChange={(e) => setFormData({ ...formData, lowLabel: e.target.value })}
                placeholder="e.g., Not at all"
              />
              <InputField
                label="High End Label (5)"
                value={formData.highLabel}
                onChange={(e) => setFormData({ ...formData, highLabel: e.target.value })}
                placeholder="e.g., Very much"
              />
            </div>
          )}

          {formData.questionType === "multiple_choice" && (
            <fieldset>
              <legend className="mb-1.5 block text-sm font-medium text-fg">Answer Options</legend>
              <ul className="flex flex-col gap-2">
                {formData.options.map((option, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <span className="flex-1 rounded-control bg-surface-subtle px-3 py-2 text-sm text-fg">
                      {option}
                    </span>
                    <IconButton
                      icon={<MdClose />}
                      label={`Remove ${option}`}
                      size="sm"
                      className="text-danger-fg"
                      onClick={() => removeOption(index)}
                    />
                  </li>
                ))}
              </ul>
              <div className="mt-2 flex items-start gap-2">
                <InputField
                  aria-label="New option"
                  className="flex-1"
                  value={newOption}
                  onChange={(e) => setNewOption(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addOption();
                    }
                  }}
                  placeholder="Add an option"
                />
                <Button variant="secondary" onClick={addOption}>
                  Add
                </Button>
              </div>
            </fieldset>
          )}
        </form>
      </Dialog>
    </>
  );
}
